/**
 * ================================================================================
 * PAGES LOCALES SEO - Épicerie par Ville
 * ================================================================================
 * Copyright (c) 2024-2025 Abdelghani Baaloulou - Tous droits réservés
 * 
 * Pages de destination optimisées pour le référencement local
 * Cible : "épicerie [ville]", "livraison [ville]", etc.
 * ================================================================================
 */

import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Clock, Phone, Truck, Star, Gift, CreditCard, 
  CheckCircle, ArrowRight, Navigation, ShoppingBag, Sparkles,
  Heart, Gamepad2, Users, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';
import SnapchatSection from '../components/SnapchatSection';

// ============================================
// 📍 DONNÉES DES VILLES (15km autour de Vermelles)
// ============================================

const CITIES_DATA = {
  'vermelles': {
    name: 'Vermelles',
    distance: 0,
    population: '4 500',
    codePostal: '62980',
    deliveryTime: '15-25 min',
    description: 'Notre magasin est situé au cœur de Vermelles, au 138 Rue Arthur Lamendin.',
    specificText: 'Venez nous rendre visite directement en boutique ou commandez en ligne pour une livraison express !'
  },
  'noyelles-les-vermelles': {
    name: 'Noyelles-lès-Vermelles',
    distance: 2,
    population: '2 100',
    codePostal: '62980',
    deliveryTime: '10-15 min',
    description: 'À seulement 2km de notre magasin, Noyelles-lès-Vermelles bénéficie de notre livraison la plus rapide.',
    specificText: 'Nos livreurs connaissent parfaitement le secteur pour une livraison ultra-rapide !'
  },
  'mazingarbe': {
    name: 'Mazingarbe',
    distance: 3,
    population: '7 500',
    codePostal: '62670',
    deliveryTime: '15-20 min',
    description: 'Mazingarbe est l\'une des villes les plus proches de notre épicerie.',
    specificText: 'Livraison gratuite dès 25€ d\'achat pour tous les habitants de Mazingarbe !'
  },
  'annequin': {
    name: 'Annequin',
    distance: 4,
    population: '2 800',
    codePostal: '62149',
    deliveryTime: '15-25 min',
    description: 'Annequin fait partie de notre zone de livraison prioritaire.',
    specificText: 'Commandez avant 22h pour une livraison le soir même !'
  },
  'hulluch': {
    name: 'Hulluch',
    distance: 4,
    population: '3 200',
    codePostal: '62410',
    deliveryTime: '15-25 min',
    description: 'Les habitants de Hulluch profitent de notre service de livraison rapide.',
    specificText: 'Épicerie complète livrée chez vous en moins de 30 minutes !'
  },
  'cambrin': {
    name: 'Cambrin',
    distance: 5,
    population: '1 800',
    codePostal: '62149',
    deliveryTime: '20-30 min',
    description: 'Cambrin bénéficie de notre service de livraison 7 jours sur 7.',
    specificText: 'Ouverts tard le week-end, nous livrons jusqu\'à 2h du matin !'
  },
  'labourse': {
    name: 'Labourse',
    distance: 6,
    population: '2 300',
    codePostal: '62113',
    deliveryTime: '20-30 min',
    description: 'Labourse fait partie de notre zone de livraison privilégiée.',
    specificText: 'Livraison rapide garantie pour tous les habitants de Labourse !'
  },
  'sailly-labourse': {
    name: 'Sailly-Labourse',
    distance: 6,
    population: '2 100',
    codePostal: '62113',
    deliveryTime: '20-30 min',
    description: 'Sailly-Labourse, village paisible, est desservi régulièrement.',
    specificText: 'Service de proximité pour les habitants de Sailly-Labourse !'
  },
  'cuinchy': {
    name: 'Cuinchy',
    distance: 7,
    population: '1 900',
    codePostal: '62149',
    deliveryTime: '25-35 min',
    description: 'Cuinchy profite de notre service d\'épicerie avec livraison à domicile.',
    specificText: 'Livraison disponible tous les jours pour Cuinchy !'
  },
  'auchy-les-mines': {
    name: 'Auchy-les-Mines',
    distance: 8,
    population: '5 800',
    codePostal: '62138',
    deliveryTime: '25-40 min',
    description: 'Auchy-les-Mines est couverte par notre réseau de livraison.',
    specificText: 'Des centaines de clients satisfaits à Auchy-les-Mines !'
  },
  'festubert': {
    name: 'Festubert',
    distance: 7,
    population: '1 500',
    codePostal: '62149',
    deliveryTime: '25-35 min',
    description: 'Festubert, village historique, fait partie de notre zone de livraison.',
    specificText: 'Livraison disponible même dans les hameaux de Festubert !'
  },
  'givenchy-les-la-bassee': {
    name: 'Givenchy-lès-la-Bassée',
    distance: 8,
    population: '3 200',
    codePostal: '62149',
    deliveryTime: '25-40 min',
    description: 'Givenchy-lès-la-Bassée bénéficie de notre service de livraison.',
    specificText: 'Livraison express pour tous les quartiers de Givenchy !'
  },
  'loos-en-gohelle': {
    name: 'Loos-en-Gohelle',
    distance: 5,
    population: '6 800',
    codePostal: '62750',
    deliveryTime: '20-30 min',
    description: 'Loos-en-Gohelle, ville écologique par excellence, fait partie de notre zone de livraison.',
    specificText: 'Livraison éco-responsable pour les habitants de Loos-en-Gohelle !'
  },
  'grenay': {
    name: 'Grenay',
    distance: 6,
    population: '6 500',
    codePostal: '62160',
    deliveryTime: '20-30 min',
    description: 'Grenay profite de notre service d\'épicerie avec livraison à domicile.',
    specificText: 'Programme de fidélité avantageux : 1 tampon = des réductions !'
  },
  'bully-les-mines': {
    name: 'Bully-les-Mines',
    distance: 6,
    population: '12 000',
    codePostal: '62160',
    deliveryTime: '20-35 min',
    description: 'Grande ville minière, Bully-les-Mines est couverte par notre livraison.',
    specificText: 'Plus de 500 clients fidèles à Bully-les-Mines nous font confiance !'
  },
  'beuvry': {
    name: 'Beuvry',
    distance: 7,
    population: '9 200',
    codePostal: '62660',
    deliveryTime: '25-35 min',
    description: 'Beuvry et ses environs sont desservis quotidiennement par nos livreurs.',
    specificText: 'Livraison gratuite pour les commandes de plus de 30€ !'
  },
  'sains-en-gohelle': {
    name: 'Sains-en-Gohelle',
    distance: 7,
    population: '5 800',
    codePostal: '62114',
    deliveryTime: '25-35 min',
    description: 'Sains-en-Gohelle fait partie de notre réseau de livraison quotidien.',
    specificText: 'Jeux et programme de fidélité disponibles pour tous nos clients !'
  },
  'noeux-les-mines': {
    name: 'Nœux-les-Mines',
    distance: 8,
    population: '12 500',
    codePostal: '62290',
    deliveryTime: '25-40 min',
    description: 'Nœux-les-Mines, ville dynamique du bassin minier, est livrée 7j/7.',
    specificText: 'Rejoignez les centaines de clients de Nœux qui nous font confiance !'
  },
  'bethune': {
    name: 'Béthune',
    distance: 9,
    population: '25 000',
    codePostal: '62400',
    deliveryTime: '30-45 min',
    description: 'Béthune, chef-lieu d\'arrondissement, bénéficie de notre service premium.',
    specificText: 'Service de livraison étendu pour couvrir tout Béthune et ses quartiers !'
  },
  'lens': {
    name: 'Lens',
    distance: 10,
    population: '31 000',
    codePostal: '62300',
    deliveryTime: '30-45 min',
    description: 'Lens, capitale du bassin minier, profite de notre livraison express.',
    specificText: 'Livraison disponible même les soirs de match au stade Bollaert !'
  },
  'harnes': {
    name: 'Harnes',
    distance: 11,
    population: '13 000',
    codePostal: '62440',
    deliveryTime: '35-50 min',
    description: 'Harnes est incluse dans notre zone de livraison élargie.',
    specificText: 'Commandez tôt pour une livraison garantie le jour même !'
  },
  'wingles': {
    name: 'Wingles',
    distance: 8,
    population: '8 200',
    codePostal: '62410',
    deliveryTime: '25-35 min',
    description: 'Wingles profite d\'un accès direct depuis notre épicerie.',
    specificText: 'Zone industrielle et résidentielle couvertes par notre service !'
  },
  'douvrin': {
    name: 'Douvrin',
    distance: 6,
    population: '5 300',
    codePostal: '62138',
    deliveryTime: '20-30 min',
    description: 'Douvrin est rapidement accessible depuis Vermelles.',
    specificText: 'Livraison rapide pour tous les quartiers de Douvrin !'
  },
  'billy-berclau': {
    name: 'Billy-Berclau',
    distance: 7,
    population: '4 800',
    codePostal: '62138',
    deliveryTime: '25-35 min',
    description: 'Billy-Berclau fait partie de notre réseau de livraison quotidien.',
    specificText: 'Proximité avec l\'usine Renault : livraison adaptée aux horaires décalés !'
  },
  'violaines': {
    name: 'Violaines',
    distance: 5,
    population: '4 100',
    codePostal: '62138',
    deliveryTime: '20-25 min',
    description: 'Violaines, village proche de Vermelles, est desservi rapidement.',
    specificText: 'Livraison express pour nos voisins de Violaines !'
  },
  'lievin': {
    name: 'Liévin',
    distance: 12,
    population: '31 000',
    codePostal: '62800',
    deliveryTime: '35-50 min',
    description: 'Liévin, grande ville du bassin minier, est incluse dans notre zone de livraison.',
    specificText: 'Livraison disponible pour tous les quartiers de Liévin !'
  },
  'sallaumines': {
    name: 'Sallaumines',
    distance: 10,
    population: '10 000',
    codePostal: '62430',
    deliveryTime: '30-45 min',
    description: 'Sallaumines bénéficie de notre service de livraison quotidien.',
    specificText: 'Service rapide pour tous les habitants de Sallaumines !'
  },
  'vendin-le-vieil': {
    name: 'Vendin-le-Vieil',
    distance: 9,
    population: '7 500',
    codePostal: '62880',
    deliveryTime: '30-40 min',
    description: 'Vendin-le-Vieil fait partie de notre réseau de livraison.',
    specificText: 'Livraison adaptée aux horaires de la zone commerciale !'
  }
};

// Liste des villes pour la navigation
const ALL_CITIES = Object.keys(CITIES_DATA);

// ============================================
// 🎨 COMPOSANT PAGE LOCALE
// ============================================

export default function LocalCityPage() {
  const { city } = useParams();
  const navigate = useNavigate();
  
  // Normaliser le slug de la ville
  const citySlug = city?.toLowerCase().replace(/\s+/g, '-');
  const cityData = CITIES_DATA[citySlug];
  
  // Rediriger si ville non trouvée
  useEffect(() => {
    if (city && !cityData) {
      navigate('/epicerie/vermelles');
    }
  }, [city, cityData, navigate]);
  
  if (!cityData) {
    return null;
  }

  // 🔥 SEO ULTRA-OPTIMISÉ POUR GOOGLE - ÉPICERIE LOCALE !
  const pageTitle = `Épicerie ${cityData.name} (${cityData.codePostal}) | Livraison à Domicile 7j/7 | La Petite Épicerie`;
  
  const pageDescription = `🛒 Épicerie avec livraison à ${cityData.name} (${cityData.codePostal}). Ouverts 7j/7 de 12h à minuit, livraison jusqu'à 2h du matin ! ⏱️ Livraison en ${cityData.deliveryTime}. Boissons, snacks, produits frais, courses de dépannage. Programme fidélité avec cadeaux et réductions. ${cityData.description}`;

  // 🎯 MOTS-CLÉS SEO ÉPICERIE - ULTRA COMPLET !
  const epicerieKeywords = [
    // Recherches principales épicerie
    `épicerie ${cityData.name}`,
    `épicerie de nuit ${cityData.name}`,
    `épicerie ouverte tard ${cityData.name}`,
    `épicerie ouverte le dimanche ${cityData.name}`,
    `superette ${cityData.name}`,
    `supérette ${cityData.name}`,
    `alimentation ${cityData.name}`,
    `alimentation générale ${cityData.name}`,
    // Livraison
    `livraison courses ${cityData.name}`,
    `livraison à domicile ${cityData.name}`,
    `livraison épicerie ${cityData.name}`,
    `livraison nuit ${cityData.name}`,
    `livraison tard ${cityData.name}`,
    `se faire livrer ${cityData.name}`,
    `courses livrées ${cityData.name}`,
    // Produits spécifiques
    `livraison boissons ${cityData.name}`,
    `livraison snacks ${cityData.name}`,
    `livraison alcool ${cityData.name}`,
    `livraison cigarettes ${cityData.name}`,
    `livraison bières ${cityData.name}`,
    `livraison sodas ${cityData.name}`,
    // Urgence / dépannage
    `courses dépannage ${cityData.name}`,
    `courses urgentes ${cityData.name}`,
    `épicerie 24h ${cityData.name}`,
    `magasin ouvert tard ${cityData.name}`,
    `où acheter ${cityData.name} soir`,
    // Géolocalisation
    `${cityData.codePostal}`,
    `épicerie ${cityData.codePostal}`,
    `livraison ${cityData.codePostal}`,
    `courses ${cityData.codePostal}`,
    // Concurrence locale
    `meilleure épicerie ${cityData.name}`,
    `épicerie pas cher ${cityData.name}`,
    `épicerie proche ${cityData.name}`,
    `épicerie à côté ${cityData.name}`
  ].join(', ');

  // 🔥 SEO RENCONTRES (bonus)
  const rencontresKeywords = `rencontre ${cityData.name}, célibataire ${cityData.name}, dating ${cityData.name}, app rencontre ${cityData.name}`;

  return (
    <>
      {/* 🔥 SEO META TAGS - ULTRA OPTIMISÉ GOOGLE ! */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${epicerieKeywords}, ${rencontresKeywords}`} />
        <link rel="canonical" href={`https://la-petite-epicerie.shop/epicerie/${citySlug}`} />
        
        {/* Robots */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        {/* Open Graph - Facebook/LinkedIn */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`https://la-petite-epicerie.shop/epicerie/${citySlug}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="La Petite Épicerie" />
        <meta property="og:image" content="https://la-petite-epicerie.shop/og-image-epicerie.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="FR-62" />
        <meta name="geo.placename" content={cityData.name} />
        <meta name="geo.position" content="50.4833;2.7167" />
        <meta name="ICBM" content="50.4833, 2.7167" />
        
        {/* Schema.org LocalBusiness - ENRICHI ! */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "GroceryStore",
            "name": "La Petite Épicerie - Livraison ${cityData.name}",
            "alternateName": "Épicerie ${cityData.name}",
            "url": "https://la-petite-epicerie.shop/epicerie/${citySlug}",
            "description": "${pageDescription.replace(/"/g, '\\"')}",
            "image": "https://la-petite-epicerie.shop/og-image-epicerie.jpg",
            "telephone": "+33780648976",
            "email": "contact@la-petite-epicerie.shop",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "138 Rue Arthur Lamendin",
              "addressLocality": "Vermelles",
              "postalCode": "62980",
              "addressRegion": "Hauts-de-France",
              "addressCountry": "FR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 50.4833,
              "longitude": 2.7167
            },
            "areaServed": [
              {
                "@type": "City",
                "name": "${cityData.name}",
                "postalCode": "${cityData.codePostal}"
              }
            ],
            "priceRange": "€",
            "currenciesAccepted": "EUR",
            "paymentAccepted": "Cash, Credit Card, Debit Card",
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Sunday"],
                "opens": "12:00",
                "closes": "00:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Friday", "Saturday"],
                "opens": "12:00",
                "closes": "02:00"
              }
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Produits disponibles",
              "itemListElement": [
                {"@type": "Offer", "itemOffered": {"@type": "Product", "name": "Boissons fraîches"}},
                {"@type": "Offer", "itemOffered": {"@type": "Product", "name": "Snacks et confiseries"}},
                {"@type": "Offer", "itemOffered": {"@type": "Product", "name": "Produits d'épicerie"}},
                {"@type": "Offer", "itemOffered": {"@type": "Product", "name": "Livraison à domicile"}}
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "127",
              "bestRating": "5",
              "worstRating": "1"
            },
            "sameAs": [
              "https://www.facebook.com/lapetiteepicerievermelles",
              "https://www.instagram.com/lapetiteepicerie62"
            ]
          }
        `}</script>
        
        {/* Schema.org Service - LIVRAISON */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Livraison de courses à domicile",
            "provider": {
              "@type": "LocalBusiness",
              "name": "La Petite Épicerie"
            },
            "areaServed": {
              "@type": "City",
              "name": "${cityData.name}"
            },
            "description": "Livraison de courses à domicile à ${cityData.name} en ${cityData.deliveryTime}. Service disponible 7j/7.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "EUR",
              "description": "Livraison gratuite"
            }
          }
        `}</script>
        
        {/* Schema.org FAQ - Questions fréquentes */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Est-ce que vous livrez à ${cityData.name} ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Oui ! Nous livrons à ${cityData.name} (${cityData.codePostal}) en ${cityData.deliveryTime}. Notre service de livraison est disponible 7 jours sur 7."
                }
              },
              {
                "@type": "Question",
                "name": "Quels sont les horaires de livraison à ${cityData.name} ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Nous livrons à ${cityData.name} tous les jours de 12h à minuit, et jusqu'à 2h du matin le vendredi et samedi soir !"
                }
              },
              {
                "@type": "Question",
                "name": "Quel est le minimum de commande pour ${cityData.name} ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Le minimum de commande varie selon la zone. ${cityData.name} étant à ${cityData.distance}km, contactez-nous pour connaître le montant minimum. La livraison est GRATUITE !"
                }
              },
              {
                "@type": "Question",
                "name": "Quels produits livrez-vous à ${cityData.name} ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Nous livrons tout notre catalogue : boissons fraîches, snacks, confiseries, produits d'épicerie, et bien plus. Parfait pour vos soirées et courses de dépannage !"
                }
              }
            ]
          }
        `}</script>
        
        {/* BreadcrumbList */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Accueil",
                "item": "https://la-petite-epicerie.shop"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Livraison",
                "item": "https://la-petite-epicerie.shop/livraison"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Épicerie ${cityData.name}",
                "item": "https://la-petite-epicerie.shop/epicerie/${citySlug}"
              }
            ]
          }
        `}</script>
      </Helmet>

      <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-br from-[#0a0015] via-[#1a0033] to-[#0a0015] text-white">
        {/* Hero Section */}
        <section className="relative py-16 px-4 overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 text-green-400 text-sm mb-6">
                <CheckCircle className="w-4 h-4" />
                Livraison disponible à {cityData.name}
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-6xl font-bold mb-4">
                Épicerie à <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500">{cityData.name}</span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Votre épicerie de quartier livre à {cityData.name} ({cityData.codePostal}) 7 jours sur 7. 
                Livraison en <span className="text-green-400 font-semibold">{cityData.deliveryTime}</span> !
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/livraison">
                  <Button className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-xl">
                    <Truck className="w-5 h-5 mr-2" />
                    🚚 Commander une livraison
                  </Button>
                </Link>
                <a href="tel:+33780648976">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl">
                    <Phone className="w-5 h-5 mr-2" />
                    07 80 64 89 76
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 px-4 bg-black/30">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-2xl bg-white/5">
                <MapPin className="w-8 h-8 text-pink-500 mx-auto mb-2" />
                <div className="text-3xl font-bold">{cityData.distance}km</div>
                <div className="text-gray-400 text-sm">de notre magasin</div>
              </div>
              <div className="text-center p-6 rounded-2xl bg-white/5">
                <Clock className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <div className="text-3xl font-bold">{cityData.deliveryTime}</div>
                <div className="text-gray-400 text-sm">temps de livraison</div>
              </div>
              <div className="text-center p-6 rounded-2xl bg-white/5">
                <Truck className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <div className="text-3xl font-bold">7j/7</div>
                <div className="text-gray-400 text-sm">livraison disponible</div>
              </div>
              <div className="text-center p-6 rounded-2xl bg-white/5">
                <Star className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <div className="text-3xl font-bold">5.0/5</div>
                <div className="text-gray-400 text-sm">note Google (26 avis)</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Pourquoi choisir La Petite Épicerie à {cityData.name} ?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: Truck,
                  title: 'Livraison express',
                  description: `Livraison à ${cityData.name} en ${cityData.deliveryTime}. Nos livreurs connaissent parfaitement votre secteur.`,
                  color: 'text-green-500'
                },
                {
                  icon: Clock,
                  title: 'Ouverts tard',
                  description: 'De 12h à minuit en semaine, jusqu\'à 2h du matin le vendredi et samedi. Parfait pour vos soirées !',
                  color: 'text-purple-500'
                },
                {
                  icon: Gift,
                  title: 'Programme fidélité',
                  description: 'Cumulez des tampons et gagnez des réductions. Plus vous commandez, plus vous économisez !',
                  color: 'text-pink-500'
                },
                {
                  icon: Gamepad2,
                  title: 'Jeux & divertissement',
                  description: 'Roue de la fortune, grattage, snake... Jouez et gagnez des réductions sur vos prochaines commandes !',
                  color: 'text-yellow-500'
                },
                {
                  icon: Heart,
                  title: 'Heartbeats 💜',
                  description: 'Notre module de rencontres exclusif. Faites des rencontres avec les gens de votre quartier !',
                  color: 'text-red-500'
                },
                {
                  icon: CreditCard,
                  title: 'Paiement flexible',
                  description: 'CB, espèces, PayPal... Payez comme vous voulez, même à la livraison !',
                  color: 'text-blue-500'
                }
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-6 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <feature.icon className={`w-10 h-10 ${feature.color} mb-4`} />
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ⭐ SECTION TÉMOIGNAGES - Preuve sociale ! */}
        <section className="py-16 px-4 bg-black/20">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-3">
                ⭐ Ce que disent nos clients
              </h2>
              <div className="flex items-center justify-center gap-2">
                <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-5 h-5" />
                <span className="text-yellow-400 font-bold text-xl">5.0</span>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-400">• 26 avis</span>
              </div>
              <p className="text-pink-400 text-sm mt-1">★ Note parfaite sur Google ! ★</p>
            </div>

            {/* Grille des témoignages */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Témoignage Ynn Wll */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold">
                    Y
                  </div>
                  <div>
                    <div className="font-medium text-white">Ynn Wll</div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-4 h-4 ml-auto" />
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "Une épicerie de quartier qui <strong className="text-cyan-400">dynamise le quotidien</strong> ! Prix très intéressants, promotions régulières. Service de livraison rapide et efficace. <strong className="text-pink-400">Je recommande sans hésiter !</strong>"
                </p>
              </motion.div>

              {/* Témoignage RBL - "dinguerie" */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center text-white font-bold">
                    R
                  </div>
                  <div>
                    <div className="font-medium text-white">RBL</div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-4 h-4 ml-auto" />
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "Je recommande. Le seul magasin où je trouve ma boisson préférée, <strong className="text-pink-400">c'est juste une dinguerie !</strong> 🍑"
                </p>
              </motion.div>

              {/* Témoignage Pikafury */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-white font-bold">
                    P
                  </div>
                  <div>
                    <div className="font-medium text-white">Pikafury</div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-4 h-4 ml-auto" />
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "Superbe épicerie, <strong className="text-cyan-400">jamais déçu</strong> ! Personnel serviable et agréable. Énormément de choix. <strong className="text-pink-400">Je vous la recommande fortement</strong> ❤️"
                </p>
              </motion.div>

              {/* Témoignage Rudy Krol */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold">
                    R
                  </div>
                  <div>
                    <div className="font-medium text-white">Rudy Krol</div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-4 h-4 ml-auto" />
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "Super épicerie, offre complète et <strong className="text-cyan-400">accueil sympathique</strong>. Génial ! ❤️"
                </p>
              </motion.div>

              {/* Témoignage Jojo Barjot */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                    J
                  </div>
                  <div>
                    <div className="font-medium text-white">Jojo Barjot</div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-4 h-4 ml-auto" />
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "Vendeur super sympa et <strong className="text-cyan-400">grand choix de produits</strong>, je recommande fortement"
                </p>
              </motion.div>

              {/* Témoignage Claire */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="p-5 rounded-2xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                    C
                  </div>
                  <div>
                    <div className="font-medium text-white">Claire</div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-4 h-4 ml-auto" />
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "Super sympa et serviable ! <strong className="text-pink-400">On trouve tout ce que l'on veut</strong> ! 😉"
                </p>
              </motion.div>
            </div>

            {/* Mini témoignages */}
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {[
                { name: 'Juliette', text: 'Meilleure épicerie !' },
                { name: 'Steven', text: 'Toujours au top' },
                { name: 'Martin', text: 'Excellent' },
                { name: 'Lukas', text: 'Rapide !' },
                { name: 'Hugo', text: 'Local Guide' }
              ].map((review, idx) => (
                <span 
                  key={idx}
                  className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-gray-400"
                >
                  <span className="text-yellow-400">★</span> {review.name}: "{review.text}"
                </span>
              ))}
            </div>

            {/* Lien Google */}
            <div className="text-center mt-6">
              <a 
                href="https://www.google.com/search?q=la+petite+epicerie+vermelles+avis"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-cyan-400 transition-colors"
              >
                Voir tous les avis sur Google →
              </a>
            </div>
          </div>
        </section>

        {/* Specific City Content */}
        <section className="py-16 px-4 bg-gradient-to-r from-pink-500/10 to-purple-500/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              🚚 Livraison à {cityData.name}
            </h2>
            <p className="text-xl text-gray-300 mb-4">
              {cityData.description}
            </p>
            <p className="text-lg text-green-400 font-medium">
              {cityData.specificText}
            </p>
            
            <div className="mt-8 p-6 rounded-2xl bg-black/30 inline-block">
              <div className="flex items-center gap-4 text-left">
                <Navigation className="w-12 h-12 text-pink-500" />
                <div>
                  <div className="font-semibold text-lg">Notre adresse</div>
                  <div className="text-gray-400">138 Rue Arthur Lamendin</div>
                  <div className="text-gray-400">62980 Vermelles</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 💜🔥 SECTION HEARTBEATS - PARTENAIRE EXCLUSIF "SECRET" ! */}
        <section className="py-16 px-4 relative overflow-hidden">
          {/* Background magique */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/30 via-pink-900/30 to-purple-900/30" />
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div 
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-500/20 rounded-full blur-3xl" 
            />
          </div>
          
          <div className="max-w-5xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              {/* Badge "Nouveau" */}
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/20 border border-pink-500/40 text-pink-300 text-sm mb-6"
              >
                <Heart className="w-4 h-4 fill-pink-400" />
                <span>Exclusivité {cityData.name}</span>
                <Sparkles className="w-4 h-4" />
              </motion.div>
              
              {/* Titre accrocheur */}
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                <span className="text-white">Célibataire à </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">{cityData.name}</span>
                <span className="text-white"> ?</span>
              </h2>
              
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Notre épicerie cache un secret... 💜 Une <strong>communauté de rencontres exclusive</strong> réservée à nos clients ! 
                <br />
                <span className="text-pink-300">L'amour est peut-être au coin de la rue... littéralement.</span>
              </p>
              
              {/* Cards features */}
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                <motion.div
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="p-6 rounded-2xl bg-white/5 backdrop-blur border border-white/10"
                >
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Voisins Célibataires</h3>
                  <p className="text-gray-400 text-sm">
                    Rencontre des gens de {cityData.name} et des environs qui font leurs courses chez nous !
                  </p>
                </motion.div>
                
                <motion.div
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="p-6 rounded-2xl bg-white/5 backdrop-blur border border-white/10"
                >
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Rencontres IRL</h3>
                  <p className="text-gray-400 text-sm">
                    Pas de fake ! Croise vraiment les gens au magasin. L'amour au coin de la rue, pour de vrai.
                  </p>
                </motion.div>
                
                <motion.div
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="p-6 rounded-2xl bg-white/5 backdrop-blur border border-white/10"
                >
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center mx-auto mb-4">
                    <Gamepad2 className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">100% Gratuit</h3>
                  <p className="text-gray-400 text-sm">
                    Jeux, salons vocaux, matchs... Tout est gratuit pour nos clients fidèles !
                  </p>
                </motion.div>
              </div>
              
              {/* Pixie teaser */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                className="inline-flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30"
              >
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-3xl">
                    🧚
                  </div>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-pink-500 rounded-full flex items-center justify-center text-xs"
                  >
                    💜
                  </motion.div>
                </div>
                <div className="text-left">
                  <div className="font-bold text-pink-300">Pixie, ta Commandante de Bord</div>
                  <div className="text-sm text-gray-300">
                    "Hey {cityData.name} ! 💜 Inscris-toi chez La Petite Épicerie et découvre qui te croise au quotidien..."
                  </div>
                </div>
              </motion.div>
              
              {/* CTA Principal */}
              <motion.div 
                className="mt-10"
                whileHover={{ scale: 1.02 }}
              >
                <Link to="/client">
                  <Button className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-10 py-7 text-xl rounded-2xl font-bold shadow-2xl shadow-pink-500/30">
                    <Heart className="w-6 h-6 mr-3 fill-white" />
                    Créer mon compte Client-Malin
                    <ArrowRight className="w-6 h-6 ml-3" />
                  </Button>
                </Link>
                <p className="text-gray-400 text-sm mt-4">
                  ✨ Accès Heartbeats inclus avec ton compte fidélité gratuit
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Other Cities */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">
              Nous livrons aussi dans ces villes
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {ALL_CITIES.filter(c => c !== citySlug).slice(0, 12).map(slug => (
                <Link
                  key={slug}
                  to={`/epicerie/${slug}`}
                  className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-sm"
                >
                  {CITIES_DATA[slug].name}
                </Link>
              ))}
            </div>
            
            {/* 💜 Lien discret vers les rencontres dans les autres villes */}
            <p className="text-center text-gray-500 text-sm mt-6">
              💜 Des célibataires dans chaque ville... <Link to="/client" className="text-pink-400 hover:text-pink-300 underline">Rejoins la communauté</Link>
            </p>
          </div>
        </section>

        {/* Final CTA - MEGA MAGNÉTIQUE ! */}
        <section className="py-16 px-4 bg-gradient-to-r from-pink-500 to-purple-600 relative overflow-hidden">
          {/* Étoiles animées */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute text-white/30"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              >
                ✨
              </motion.div>
            ))}
          </div>
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-4xl font-bold mb-4"
            >
              🛒 Commander + 💜 Rencontrer à {cityData.name}
            </motion.h2>
            <p className="text-xl text-white/90 mb-8">
              Un seul compte pour tout : courses livrées + accès à la communauté de célibataires du quartier !
              <br />
              <span className="text-yellow-200 font-semibold">10% offerts sur ta 1ère commande 🎁</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/client">
                <Button className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-xl font-semibold shadow-xl">
                  <Heart className="w-5 h-5 mr-2 fill-purple-500" />
                  Créer mon compte gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/offre-bienvenue">
                <Button variant="outline" className="border-white text-white hover:bg-white/20 px-8 py-6 text-lg rounded-xl font-semibold">
                  <Gift className="w-5 h-5 mr-2" />
                  Voir l'offre de bienvenue
                </Button>
              </Link>
            </div>
            
            {/* Stats impressionnantes */}
            <div className="flex justify-center gap-8 mt-10 text-white/80">
              <div className="text-center">
                <div className="text-3xl font-bold">500+</div>
                <div className="text-sm">Clients actifs</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">💜 54</div>
                <div className="text-sm">Célibataires en ligne</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">15 min</div>
                <div className="text-sm">Livraison moyenne</div>
              </div>
            </div>
          </div>
        </section>

        {/* 👻 Section Snapchat */}
        <SnapchatSection className="bg-gradient-to-b from-transparent to-black/30" />

        {/* Footer */}
        <footer className="py-8 px-4 bg-black/50 text-center text-gray-400 text-sm">
          <p>© 2025 La Petite Épicerie - Tous droits réservés</p>
          <p className="mt-2">
            Épicerie à {cityData.name} | Livraison {cityData.name} | {cityData.codePostal}
          </p>
        </footer>
      </div>
    </>
  );
}

// Export de la liste des villes pour le sitemap
export { CITIES_DATA, ALL_CITIES };
